import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { fetchBySlug } from '../lib/cms';
import useDocumentMeta from '../hooks/useDocumentMeta';
import { Shield, Users, Layers, Heart, CheckCircle2 } from 'lucide-react';

export default function Beskaeftigelsen() {
  useDocumentMeta(
    'Beskæftigelsen - hvem er vi | LIVSLYST ApS',
    'LIVSLYST ApS tilbyder beskyttet beskæftigelse efter §103 for borgere med nedsat funktionsevne i trygge, pædagogiske og udviklende rammer i Brørup.'
  );

  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadPage() {
      const data = await fetchBySlug('da', 'beskaeftigelsen');
      setPageData(data);
      setLoading(false);
    }
    loadPage();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh] bg-stone-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  const title = pageData?.title?.da || pageData?.title || 'Beskæftigelsen - hvem er vi';
  const intro = pageData?.intro?.da || pageData?.intro || '';
  const content = pageData?.content?.da || pageData?.content || '';
  const image = pageData?.image?.[0] || 'https://assets.auxodev.dk/assets/8eff581d-09aa-4f3c-b275-323f2d379ce9/ae28e25b-a6bd-4308-ad3d-758bb1aedd2c.jpeg';

  const highlights = [
    {
      title: 'Trygge rammer',
      description: 'En forudsigelig hverdag i rolige omgivelser, der giver plads til ro og fordybelse.',
      icon: Shield,
    },
    {
      title: 'Varmt fællesskab',
      description: 'Vi vægter det sociale samvær, fælles pauser og gensidig respekt utrolig højt.',
      icon: Users,
    },
    {
      title: 'Struktur i hverdagen',
      description: 'Visuel støtte og faste rutiner sikrer en genkendelig og tryg arbejdsdag.',
      icon: Layers,
    },
    {
      title: 'Meningsfuld beskæftigelse',
      description: 'Opgaverne tilpasses den enkelte, så alle oplever succes og stolthed.',
      icon: Heart,
    },
  ];

  return (
    <div className="bg-stone-50 text-stone-800 antialiased overflow-x-hidden">
      {/* Header Section */}
      <section className="bg-stone-100 py-16 md:py-20 border-b border-stone-200">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-bold">
            Hvem er vi?
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-stone-900 tracking-tight">
            {title}
          </h1>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto leading-relaxed">
            {intro ? (
              (() => {
                const match = intro.match(/(LIVSLYST(?:\s+ApS)?|Livslyst(?:\s+ApS)?)/i);
                if (!match) return intro;
                const index = match.index;
                const matchedText = match[0];
                return (
                  <>
                    {intro.slice(0, index)}
                    <Link to="/" className="font-semibold text-emerald-800 hover:text-emerald-900 transition-colors">
                      {matchedText}
                    </Link>
                    {intro.slice(index + matchedText.length)}
                  </>
                );
              })()
            ) : (
              <>
                <Link to="/" className="font-semibold text-emerald-800 hover:text-emerald-900 transition-colors">LIVSLYST ApS</Link> tilbyder beskyttet beskæftigelse efter §103 for borgere med nedsat funktionsevne i trygge, pædagogiske og udviklende rammer i Brørup.
              </>
            )}
          </p>
        </motion.div>
      </section>

      {/* Main Content Section */}
      <section className="py-16 md:py-24 bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-stretch">
            {/* Text Column */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="lg:col-span-7 space-y-6"
            >
              <h2 className="text-3xl font-bold text-stone-900 tracking-tight">
                Vores historie og formål
              </h2>
              <div 
                className="text-stone-600 leading-relaxed space-y-4 text-base"
                dangerouslySetInnerHTML={{ __html: content }}
              />
            </motion.div>

            {/* Image Column */}
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="lg:col-span-5 h-full"
            >
              <div className="relative h-full min-h-[400px]">
                <div className="absolute inset-0 bg-emerald-800 rounded-2xl rotate-2 scale-95 opacity-5" />
                <img
                  src={image}
                  alt="Fællesskab hos LIVSLYST"
                  className="relative z-10 w-full h-full object-cover rounded-2xl shadow-md border border-stone-200"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Highlights Section */}
      <section className="py-16 md:py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <span className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-bold">
              Kerneværdier i hverdagen
            </span>
            <h2 className="text-3xl font-bold text-stone-900 tracking-tight">
              Hvad kendetegner vores beskæftigelse?
            </h2>
            <p className="text-stone-600 text-base leading-relaxed">
              Vi arbejder målrettet på at skabe en hverdag, hvor alle trives og føler sig som en vigtig del af fællesskabet.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {highlights.map((hl, idx) => {
              const Icon = hl.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="bg-white p-8 rounded-2xl border border-stone-200 shadow-sm flex gap-5 items-start"
                >
                  <div className="p-3 bg-emerald-50 rounded-xl text-emerald-800 shrink-0">
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-lg font-bold text-stone-900">{hl.title}</h3>
                    <p className="text-stone-600 text-sm leading-relaxed">{hl.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
